import React from 'react';
import { GetEnvelopesResponse } from '@givelify/api';
import { ApiResponse, useApiRequest } from '@givelify/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useFilterParams } from 'api/hooks/useFilterParams';
import { isEqual } from 'lodash-es';
import {
    activeEnvelopesQueryKey,
    inactiveEnvelopesQueryKey,
} from '../../router';

const paramNames = ['envelopeIds'] as const;

export type EnvelopesFilterState = {
    loading: boolean;
    options: {
        id: number;
        label: string;
    }[];
    values: number[];
};

export const getEnvelopesFilterFromUrlParams = (
    searchParams: URLSearchParams,
) => {
    const envelopeIds = searchParams.get('envelopeIds');
    if (!envelopeIds || envelopeIds === 'none') {
        return [];
    }
    const splited = envelopeIds.split('_');
    if (splited.length > 0) {
        const newValues = splited
            .map((value) => parseInt(value))
            .filter((x) => !isNaN(x))
            .sort();
        return newValues;
    }
    return undefined;
};

export const useAllEnvelopesFilter = (
    doneeId: number,
    activeEnvelopesData: Promise<ApiResponse<GetEnvelopesResponse>>,
    inactiveEnvelopesData: Promise<ApiResponse<GetEnvelopesResponse>>,
) => {
    const [filterState, setFilterState] = React.useState<EnvelopesFilterState>({
        loading: true,
        options: [],
        values: [],
    });
    const [activeEnvelopes, setActiveEnvelopes] =
        React.useState<EnvelopesFilterState>({
            loading: true,
            options: [],
            values: [],
        });
    const [inactiveEnvelopes, setInactiveEnvelopes] =
        React.useState<EnvelopesFilterState>({
            loading: true,
            options: [],
            values: [],
        });
    const queryClient = useQueryClient();

    const [getAllActiveRequest] =
        useApiRequest<GetEnvelopesResponse>(activeEnvelopesData);
    const [getAllInactiveRequest] = useApiRequest<GetEnvelopesResponse>(
        inactiveEnvelopesData,
    );
    const [filter, setFilter] = useFilterParams<number[], typeof paramNames>(
        [],
        paramNames,
        (state, _paramName) => {
            if (state && state.length > 0) {
                return state.join('_');
            }
            return '';
        },
        (values) => {
            const splited = values['envelopeIds'].split('_');
            const newValues = splited
                .map((value) => parseInt(value))
                .filter((x) => !isNaN(x))
                .sort();
            return newValues;
        },
    );
    React.useEffect(() => {
        setFilterState({
            options: [],
            values: [],
            loading: true,
        });
        return () => {
            void queryClient.invalidateQueries({
                queryKey: [activeEnvelopesQueryKey],
            });
            void queryClient.invalidateQueries({
                queryKey: [inactiveEnvelopesQueryKey],
            });
        };
        // listen only to doneeId change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doneeId]);

    React.useEffect(() => {
        if (getAllActiveRequest.type === 'REQUEST_SUCCESS') {
            const options = getAllActiveRequest.response.data.map(
                (envelope) => ({
                    id: envelope.id,
                    label: envelope.name,
                }),
            );
            setActiveEnvelopes({
                options,
                values: [],
                loading: false,
            });
        }
        //listen only to getRequest change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAllActiveRequest]);

    React.useEffect(() => {
        if (getAllInactiveRequest.type === 'REQUEST_SUCCESS') {
            const options = getAllInactiveRequest.response.data.map(
                (envelope) => ({
                    id: envelope.id,
                    label: envelope.name,
                }),
            );
            setInactiveEnvelopes({
                options,
                values: [],
                loading: false,
            });
        }
        //listen only to getRequest change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAllInactiveRequest]);

    React.useEffect(() => {
        if (!activeEnvelopes.loading && !inactiveEnvelopes.loading) {
            //merge active and inactive envelopes
            setFilterState({
                options: [
                    ...activeEnvelopes.options,
                    ...inactiveEnvelopes.options,
                ],
                values: filter,
                loading: false,
            });
        }
        //listen only to getRequest change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEnvelopes, inactiveEnvelopes]);

    React.useEffect(() => {
        if (!isEqual(filter, filterState.values)) {
            setFilterState({
                ...filterState,
                values: filter,
            });
        }
        //listen only to getRequest change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const onChange = (values: number[]) => {
        setFilter(values);
        setFilterState({
            ...filterState,
            values,
        });
    };

    return [filterState, onChange] as const;
};
